import type { ComponentChildren, Ref } from 'preact';
import * as stylex from '@stylexjs/stylex';
import { ArrowUpIcon } from '@xantory/icons';
import { useT } from '@xantory/i18n';
import { Button } from '../Button/button';
import { Spinner } from '../Spinner/spinner';
import { ledgerStatus, visibleRange, type LedgerProps } from './ledger.model';
import { styles } from './table-ledger.styles';
import { styles as tableStyles } from './table.styles';
import type { ResolvedColumn } from './table';
const STATUS_KEY = {
  loading: 'ledgerStatusLoading',
  fetching: 'ledgerStatusFetching',
  error: 'ledgerStatusError',
  paused: 'ledgerStatusPaused',
  done: 'ledgerStatusDone',
  limited: 'ledgerStatusLimited',
  idle: 'ledgerStatusIdle'
} as const;

/** One `<col>` per column: a column with a `width` keeps it, `100%` (or none) takes
 *  what is left. Rendered in BOTH the header table and the body table. */
export function LedgerCols<T>({
  columns
}: {
  columns: ResolvedColumn<T>[];
}) {
  return <colgroup>
      {columns.map(col => <col key={col.key} style={col.width && col.width !== '100%' ? {
      width: col.width
    } : undefined} />)}
    </colgroup>;
}

/** The ledger's header — a separate table above the scroll box (see `tableLedger`).
 *  `gutter` is the body's scrollbar width, so the two tables stay the same width.
 *  Its cells are the Table's own (`renderCell`), so sorting and filter menus work. */
export function LedgerHead<T>({
  columns,
  gutter,
  headRef,
  renderCell,
  minWidthPx
}: {
  columns: ResolvedColumn<T>[];
  gutter: number;
  minWidthPx?: number | undefined;
  /** The Table's own header-cell renderer, so a sortable or filterable column (its
   *  click-to-cycle sort, its filter menu) is the same control as in a plain table. */
  renderCell: (col: ResolvedColumn<T>, i: number) => ComponentChildren;
  /** The body copies its `scrollLeft` here, so a sideways scroll moves both. */
  headRef: Ref<HTMLDivElement>;
}) {
  return <div ref={headRef} {...stylex.props(styles.head)}>
      <table {...stylex.props(tableStyles.table, tableStyles.tableLedger)} style={{
      width: `calc(100% - ${gutter}px)`,
      ...(minWidthPx && {
        minWidth: `${minWidthPx}px`
      })
    }}>
        <LedgerCols columns={columns} />
        <thead>
          <tr>{columns.map((col, i) => renderCell(col, i))}</tr>
        </thead>
      </table>
    </div>;
}

/** "↑ N new entries" — rows that arrived while the reader was scrolled down. */
export function LedgerPill({
  ledger,
  onShow
}: {
  ledger: LedgerProps;
  onShow: () => void;
}) {
  const t = useT('ui');
  if (ledger.pending <= 0) return null;
  return <button type="button" onClick={onShow} {...stylex.props(styles.pill)}>
      <ArrowUpIcon size={14} strokeWidth={2.4} />
      {t(ledger.pending === 1 ? 'ledgerNew_one' : 'ledgerNew_many', {
      count: ledger.pending
    })}
    </button>;
}

/** What sits under the last row, inside the scroll box: a failed read, the pause
 *  at the auto-load ceiling, or the end of the list. */
export function LedgerTail({
  ledger
}: {
  ledger: LedgerProps;
}) {
  const t = useT('ui');
  return <>
      {ledger.state === 'error' && <div role="alert" {...stylex.props(styles.tail, styles.tailError)}>
          <span>{t(ledger.loaded > 0 ? 'ledgerErrorStale' : 'ledgerError', {
          count: ledger.loaded
        })}</span>
          {ledger.onRetry && <Button variant="ghost" size="sm" onClick={ledger.onRetry}>
              {t('ledgerRetry')}
            </Button>}
        </div>}
      {ledger.state === 'ready' && ledger.fetching && <div {...stylex.props(styles.tail)}>
          <Spinner size="sm" tone="inherit" />
          <span>{t('ledgerFetching', {
          count: ledger.pageSize
        })}</span>
        </div>}
      {ledger.state === 'ready' && ledger.paused && <div {...stylex.props(styles.tail, styles.tailPaused)}>
          <span>
            {ledger.more ? t('ledgerPausedMore', {
          loaded: ledger.loaded
        }) : t('ledgerPaused', {
          loaded: ledger.loaded,
          remaining: ledger.total - ledger.loaded
        })}
          </span>
          <Button variant="ghost" size="sm" onClick={ledger.onMore}>
            {t('ledgerLoadMore', {
          count: ledger.pageSize
        })}
          </Button>
        </div>}
      {ledger.state === 'ready' && ledger.ended && <div {...stylex.props(styles.tail, styles.tailEnd)}>
          <span {...stylex.props(styles.endLabel)}>
            {t(ledger.limited ? 'ledgerLimitedTitle' : 'ledgerEnd')}
          </span>
          <span>
            {ledger.limited ? t('ledgerLimitedNote', {
          total: ledger.total
        }) : ledger.earliest ? t('ledgerEndNoteSince', {
          total: ledger.total,
          earliest: ledger.earliest
        }) : t('ledgerEndNote', {
          total: ledger.total
        })}
          </span>
        </div>}
    </>;
}

/** "Rows 191–200 · 200 of 412 loaded" + progress + status + Back to newest. */
export function LedgerFooter({
  ledger,
  topRow,
  bottomRow,
  far,
  onTop
}: {
  ledger: LedgerProps;
  topRow: number;
  bottomRow: number;
  /** Scrolled far enough down that "Back to newest" is worth showing. */
  far: boolean;
  onTop: () => void;
}) {
  const t = useT('ui');
  // Nothing to count yet: while the first read is in flight the footer is just its
  // status, so the frame does not jump when the numbers arrive.
  if (ledger.total === 0 && ledger.state === 'loading') return <div {...stylex.props(styles.foot)}>
        <span {...stylex.props(styles.status)}>
          <Spinner size="sm" tone="inherit" />
          {t('ledgerStatusLoading')}
        </span>
      </div>;
  if (ledger.total === 0) return null;
  const {
    first,
    last
  } = visibleRange(topRow, bottomRow, ledger.loaded);
  const status = ledgerStatus(ledger);
  const pct = Math.round(ledger.loaded / ledger.total * 100);
  // A limited source's total is a floor, not a count: "200+", never "200".
  const totalLabel = ledger.limited || ledger.more ? `${ledger.total}+` : String(ledger.total);
  return <div {...stylex.props(styles.foot)}>
      <span {...stylex.props(styles.footRows)}>
        {t('ledgerRows')}{' '}
        <strong {...stylex.props(styles.footStrong)}>
          {first}–{last}
        </strong>{' '}
        · {t('ledgerLoaded', {
        loaded: ledger.loaded,
        total: totalLabel
      })}
      </span>
      <span role="progressbar" aria-valuemin={0} aria-valuemax={ledger.total} aria-valuenow={ledger.loaded} aria-label={t('ledgerLoaded', {
      loaded: ledger.loaded,
      total: totalLabel
    })} {...stylex.props(styles.track)}>
        <span {...stylex.props(styles.fill)} style={{
        width: `${pct}%`
      }} />
      </span>
      <span {...stylex.props(styles.status)}>
        {(status === 'loading' || status === 'fetching') && <Spinner size="sm" tone="inherit" />}
        {t(STATUS_KEY[status])}
      </span>
      <span {...stylex.props(styles.footSpacer)} />
      {far && <Button variant="ghost" size="sm" onClick={onTop}>
          <ArrowUpIcon size={13} strokeWidth={2.4} />
          {t('ledgerBackToNewest')}
        </Button>}
    </div>;
}

/** The ledger's single bordered frame around header, rows and footer. Without
 *  `ledger` it renders its children untouched. */
export function LedgerFrame({
  on,
  footer,
  children
}: {
  on: boolean;
  footer: ComponentChildren;
  children: ComponentChildren;
}) {
  if (!on) return <>{children}</>;
  return <div {...stylex.props(styles.frame)}>
      {children}
      {footer}
    </div>;
}